import values from 'lodash/values';
import { bool, func, shape } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FcCheckmark } from 'react-icons/fc';
import { useSelector } from 'react-redux';
import { Alert, Button, Col, Row } from 'reactstrap';

import { ORIGINS } from '../../creative-assets/constants';
import api from '../../REST/api';
import withApiRequest from '../../REST/with-api-request';
import { flash } from '../../utils/flash';
import SingleSelect from '../SingleSelect';
import ImageCropper from './ImageCropper';
import { getCreationRequirementOptions, toDataURL } from './utils';

const endpoint = api.addCreative;
function ImageCropperWrapper({
  onRequest,
  onSuccess,
  loading,
  onDeleteCreative,
  imageData,
}) {
  const campaignLines = useSelector(
    ({ campaignEditor: { campaignLinesById } }) => values(campaignLinesById),
  );
  const opportunityId = useSelector(
    ({ campaignEditor: { campaign } }) => campaign.opportunity.id,
  );

  const creativeRequirementOptions =
    getCreationRequirementOptions(campaignLines);
  const [imageToCrop, setImageToCrop] = useState(undefined);
  const [croppedImage, setCroppedImage] = useState(undefined);
  const [finalCroppedUrl, setFinalCroppedUrl] = useState(undefined);
  const [blob, setBlob] = useState(undefined);
  const [dimensions, setDimensions] = useState(
    creativeRequirementOptions?.[0]?.value || {
      label: '',
      width: null,
      height: null,
    },
  );
  const [selectDisabled, setSelectDisabled] = useState(false);

  const { fileUrl, id, name, imageWidth, imageHeight } = imageData;

  // effect fetches image blob url on load of the modal content
  useEffect(() => {
    toDataURL(fileUrl).then(imageUrl => setImageToCrop(imageUrl));
  }, [fileUrl]);

  // disables the dropdown selection when finalCroppedUrl is null
  useEffect(() => {
    if (finalCroppedUrl) setSelectDisabled(true);
  }, [finalCroppedUrl]);

  const handleSubmit = async event => {
    event.preventDefault();
    event.stopPropagation();

    if (loading) return; // only make one request at a time

    endpoint.uploader.addFile({
      name, // file name
      type: 'image/jpg', // file type
      data: blob, // file blob
      source: 'Local', // optional, determines the source of the file, for example, Instagram.
      isRemote: false, // optional, set to true if actual file is not in the browser, but on some remote server, for example,
      // when using companion in combination with Instagram.
    });

    const meta = { opportunityId, origin: ORIGINS[0] };
    onRequest(meta)
      .then(files => {
        onSuccess(files.map(file => file.response.body));
        // Deletes the exisitng image and replaces it with the new one added
        onDeleteCreative(id);
        // Resets the internal uppy state
        endpoint.uploader.reset();
        flash('Creative modified successfully');
      })
      .catch(console.error); // eslint-disable-line no-console
  };

  const handleDimensionsChange = event => {
    setDimensions(event.target.value);
  };

  const renderWarning = () => {
    const showWarning =
      dimensions.width - imageWidth > 10 ||
      dimensions.height - imageHeight > 10;
    return showWarning ? (
      <Alert color="warning">
        <div>Warning!</div>
        <div>
          The provided image has a lower resolution which may cause loss in
          image clarity. You can upload an image with higher resolution for
          better results.
        </div>
      </Alert>
    ) : null;
  };

  return (
    <div className="image-cropper">
      <Row className="mb-3">
        <Col sm={3}>
          <div>Select an Ad Placement</div>
        </Col>
        <Col sm={9}>
          <SingleSelect
            options={creativeRequirementOptions}
            id="img-cropper"
            name="type"
            value={dimensions.label}
            onChange={handleDimensionsChange}
            readOnly={selectDisabled}
          />
        </Col>
      </Row>
      {!finalCroppedUrl && imageToCrop && (
        <div className="text-center">
          <div>
            {name} | Actual aspect ratio: {imageWidth} x {imageHeight}
          </div>
          <div className="mb-2">
            <small className="text-mute">
              Drag the selection area to fit the requested dimension
            </small>
          </div>
          <div>
            <ImageCropper
              imageToCrop={imageToCrop}
              onImageCropped={(croppedImageUrl, blobFile) => {
                setCroppedImage(croppedImageUrl);
                setBlob(blobFile);
              }}
              userDimensions={dimensions}
            />
          </div>
          <div className="mt-3">
            <Button
              color="primary"
              onClick={() => setFinalCroppedUrl(croppedImage)}
            >
              Crop
            </Button>
          </div>
        </div>
      )}
      {finalCroppedUrl && (
        <>
          {renderWarning()}
          <div className="text-center">
            <div>
              <span>
                {name} | Actual aspect ratio: {dimensions.width} x{' '}
                {dimensions.height}
              </span>
            </div>
            <div className="mb-2">
              <small className="text-success">
                <FcCheckmark />
                Image Cropped
              </small>
            </div>
            <div>
              <img className="w-100" alt="Cropped Img" src={croppedImage} />
            </div>
            <div className="mt-3">
              <Button
                className="mr-2"
                outline
                color="secondary"
                onClick={() => {
                  setFinalCroppedUrl(null);
                  setSelectDisabled(false);
                }}
              >
                Reset
              </Button>
              <Button color="primary" onClick={handleSubmit}>
                Save
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

ImageCropperWrapper.propTypes = {
  onRequest: func.isRequired,
  onSuccess: func.isRequired,
  loading: bool.isRequired,
  onDeleteCreative: func.isRequired,
  imageData: shape({}).isRequired,
};

export default withApiRequest({
  query: endpoint,
})(ImageCropperWrapper);
